import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MiniDrawer from "../Components/MiniDrawer";
import configData from "../config.json";
import "./DoctorCommunity.css";

const COMMUNITY_API = `${configData.SERVER_API_URL}/community`;
const tabs = [
  { key: "open", label: "Open Questions" },
  { key: "answered", label: "Answered Questions" },
  { key: "chats", label: "Doctor Chats" },
];

const getList = (data, keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.data?.[key])) return data.data[key];
    if (Array.isArray(data?.data?.data?.[key])) return data.data.data[key];
  }
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.data?.result)) return data.data.result;
  return [];
};

const getPatientId = (item) =>
  item?.user_id || item?.patient_user_id || item?.patient_id || item?.user?.id || item?.patient?.id;

const getConversationId = (item) =>
  item?.conversation_id || item?.conversationId || item?.community_conversation_id || item?.chat_id;

const getQuestionId = (item) =>
  item?.question_id ||
  item?.community_question_id ||
  item?.question?.id ||
  (!getConversationId(item) ? item?.id : undefined);

const getPatientName = (item) =>
  item?.patient_name ||
  item?.user_name ||
  item?.name ||
  item?.patient?.name ||
  item?.user?.name ||
  [item?.user?.fname || item?.patient?.fname, item?.user?.lname || item?.patient?.lname].filter(Boolean).join(" ") ||
  `User ${getPatientId(item) || "-"}`;

const getQuestionText = (item) =>
  item?.question || item?.question_text || item?.message || item?.content || item?.text || "No question text";

const getAnswerPreview = (item) =>
  item?.answer || item?.reply || item?.doctor_reply || item?.latest_reply || item?.last_message || "";

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401) return "Doctor authentication required";
  if (status === 403) return "You are not allowed to access this question/conversation";
  if (status === 404) return "Question or conversation not found";
  if (status === 422) {
    const errors = error?.response?.data?.errors;
    if (errors && typeof errors === "object") {
      return Object.values(errors).flat().join(", ");
    }
    return error?.response?.data?.message || "Please check the entered details";
  }
  if (status >= 500) return "Something went wrong. Please retry";
  return error?.response?.data?.message || "Something went wrong. Please retry";
};

const getTokenUserId = (token) => {
  try {
    const payload = token?.split(".")?.[1];
    if (!payload) return null;
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = JSON.parse(window.atob(normalizedPayload));
    return decodedPayload?.id || null;
  } catch (error) {
    return null;
  }
};

function DoctorCommunity() {
  const location = useLocation();
  const navigate = useNavigate();
  const profileData = location?.state?.profileData;
  const doctorToken = localStorage.getItem("doctorToken") || localStorage.getItem("token");
  const doctorId =
    profileData?.user?.id ||
    localStorage.getItem("doctorUserId") ||
    getTokenUserId(doctorToken) ||
    localStorage.getItem("userId");

  const [activeTab, setActiveTab] = useState("open");
  const [openQuestions, setOpenQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [replyText, setReplyText] = useState("");
  const [chatText, setChatText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [chatAttachment, setChatAttachment] = useState(null);
  const [fieldError, setFieldError] = useState("");
  const [pageError, setPageError] = useState("");

  const headers = useMemo(
    () => ({
      "x-user-id": doctorId,
      Accept: "application/json",
      ...(doctorToken
        ? {
            Authorization: `Bearer ${doctorToken}`,
            token: doctorToken,
          }
        : {}),
    }),
    [doctorId, doctorToken]
  );

  const fetchQuestions = useCallback(
    async (status) => {
      if (!doctorId) return;
      setQuestionLoading(true);
      setPageError("");
      try {
        const response = await axios.get(`${COMMUNITY_API}/doctor/questions`, {
          params: { status, page: 1, limit: 20 },
          headers,
        });
        const questions = getList(response.data, ["questions", "doctor_questions", "items", "results"]);
        if (status === "open") {
          setOpenQuestions(questions);
          setSelectedQuestion((current) => current || questions[0] || null);
        } else {
          setAnsweredQuestions(questions);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setPageError(message);
        toast.error(message);
      } finally {
        setQuestionLoading(false);
      }
    },
    [doctorId, headers]
  );

  const fetchMessages = useCallback(
    async (patientId) => {
      if (!doctorId || !patientId) return;
      setMessageLoading(true);
      try {
        const response = await axios.get(`${COMMUNITY_API}/doctor/messages`, {
          params: { user_id: patientId, page: 1, limit: 30 },
          headers,
        });
        setMessages(getList(response.data, ["messages", "conversation", "items", "results"]));
      } catch (error) {
        const message = getErrorMessage(error);
        setMessages([]);
        toast.error(message);
      } finally {
        setMessageLoading(false);
      }
    },
    [doctorId, headers]
  );

  useEffect(() => {
    if (!doctorId) {
      toast.error("Doctor authentication required");
      navigate("/doctor/login");
      return;
    }
    if (!doctorToken) {
      toast.error("Doctor authentication token missing. Please login again");
      navigate("/doctor/login");
      return;
    }
    fetchQuestions("open");
    fetchQuestions("answered");
  }, [doctorId, doctorToken, fetchQuestions, navigate]);

  useEffect(() => {
    if (activeTab === "chats") {
      const firstPatientId = selectedPatientId || getPatientId(openQuestions[0]) || getPatientId(answeredQuestions[0]);
      if (firstPatientId) {
        setSelectedPatientId(firstPatientId);
        setSelectedConversation(
          selectedConversation ||
            openQuestions.find((item) => String(getPatientId(item)) === String(firstPatientId)) ||
            answeredQuestions.find((item) => String(getPatientId(item)) === String(firstPatientId)) ||
            null
        );
        fetchMessages(firstPatientId);
      }
    }
  }, [activeTab, answeredQuestions, fetchMessages, openQuestions, selectedConversation, selectedPatientId]);

  const filteredOpen = openQuestions.filter((item) =>
    `${getPatientName(item)} ${getQuestionText(item)}`.toLowerCase().includes(search.toLowerCase())
  );
  const filteredAnswered = answeredQuestions.filter((item) =>
    `${getPatientName(item)} ${getQuestionText(item)} ${getAnswerPreview(item)}`.toLowerCase().includes(search.toLowerCase())
  );

  const conversations = useMemo(() => {
    const byPatient = new Map();
    [...openQuestions, ...answeredQuestions].forEach((item) => {
      const patientId = getPatientId(item);
      if (patientId && !byPatient.has(String(patientId))) {
        byPatient.set(String(patientId), item);
      }
    });
    return [...byPatient.values()].filter((item) =>
      `${getPatientName(item)} ${getQuestionText(item)}`.toLowerCase().includes(search.toLowerCase())
    );
  }, [answeredQuestions, openQuestions, search]);

  const sendDoctorReply = async ({ patientId, questionId, conversationId, message, file }) => {
    const trimmedMessage = message.trim();
    if (!patientId) {
      setFieldError("Patient user ID is missing");
      return false;
    }
    if (!questionId && !conversationId) {
      setFieldError("A question or conversation is required before replying");
      return false;
    }
    if (!trimmedMessage) {
      setFieldError("Reply message is required");
      return false;
    }

    setSending(true);
    setFieldError("");
    try {
      let body;
      let requestHeaders = headers;
      if (file) {
        body = new FormData();
        body.append("user_id", patientId);
        if (questionId) body.append("question_id", questionId);
        if (conversationId) body.append("conversation_id", conversationId);
        body.append("message", trimmedMessage);
        body.append("attachment", file);
      } else {
        body = { user_id: patientId, message: trimmedMessage };
        if (questionId) body.question_id = questionId;
        if (conversationId) body.conversation_id = conversationId;
        requestHeaders = { ...headers, "Content-Type": "application/json" };
      }

      await axios.post(`${COMMUNITY_API}/doctor/reply`, body, { headers: requestHeaders });
      toast.success("Reply sent successfully");
      await Promise.all([fetchQuestions("open"), fetchQuestions("answered")]);
      if (activeTab === "chats") await fetchMessages(patientId);
      return true;
    } catch (error) {
      const messageText = getErrorMessage(error);
      if (error?.response?.status === 422) setFieldError(messageText);
      toast.error(messageText);
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleQuestionReply = async () => {
    const sent = await sendDoctorReply({
      patientId: getPatientId(selectedQuestion),
      questionId: getQuestionId(selectedQuestion),
      conversationId: getConversationId(selectedQuestion),
      message: replyText,
      file: attachment,
    });
    if (sent) {
      setReplyText("");
      setAttachment(null);
      setSelectedQuestion(null);
    }
  };

  const handleChatSend = async () => {
    const sent = await sendDoctorReply({
      patientId: selectedPatientId,
      questionId: getQuestionId(selectedConversation),
      conversationId: getConversationId(selectedConversation),
      message: chatText,
      file: chatAttachment,
    });
    if (sent) {
      setChatText("");
      setChatAttachment(null);
    }
  };

  const renderQuestionCard = (item, status) => (
    <button
      type="button"
      key={`${status}-${getQuestionId(item)}-${getPatientId(item)}`}
      className={`doctor-community-card ${getQuestionId(selectedQuestion) === getQuestionId(item) ? "active" : ""}`}
      onClick={() => {
        setSelectedQuestion(item);
        setFieldError("");
      }}
    >
      <div className="doctor-community-card-head">
        <span className="doctor-community-patient">{getPatientName(item)}</span>
        <span className="doctor-community-time">{formatTime(item?.created_at || item?.createdAt || item?.time)}</span>
      </div>
      <p className="doctor-community-question">{getQuestionText(item)}</p>
      <span className={`doctor-community-badge ${status}`}>{status === "open" ? "Open" : "Answered"}</span>
    </button>
  );

  const renderLoading = () => (
    <>
      <div className="doctor-community-skeleton" />
      <div className="doctor-community-skeleton" />
      <div className="doctor-community-skeleton" />
    </>
  );

  return (
    <div className="doctor-community-page">
      <MiniDrawer />
      <main className="doctor-community-shell">
        <header className="doctor-community-header">
          <div>
            <h1>MyPrega Doctor Community</h1>
            <p>Manage private patient questions and doctor-only conversations</p>
          </div>
          <span className="doctor-community-id">Doctor ID: {doctorId || "-"}</span>
        </header>

        <section className="doctor-community-stats">
          <div className="doctor-community-stat"><span>Open Questions</span><strong>{openQuestions.length}</strong></div>
          <div className="doctor-community-stat"><span>Answered Questions</span><strong>{answeredQuestions.length}</strong></div>
          <div className="doctor-community-stat"><span>Active Chats</span><strong>{conversations.length}</strong></div>
        </section>

        <section className="doctor-community-panel">
          <nav className="doctor-community-tabs">
            {tabs.map((tab) => (
              <button type="button" key={tab.key} className={activeTab === tab.key ? "active" : ""} onClick={() => setActiveTab(tab.key)}>
                {tab.label}
              </button>
            ))}
          </nav>
          <div className="doctor-community-toolbar">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search patient, question, or message" />
          </div>
          {pageError && <div className="doctor-community-error">{pageError}</div>}

          {activeTab !== "chats" ? (
            <div className="doctor-community-content">
              <div className="doctor-community-list">
                {questionLoading ? renderLoading() : null}
                {!questionLoading && activeTab === "open" && filteredOpen.length === 0 ? <div className="doctor-community-empty">No open questions</div> : null}
                {!questionLoading && activeTab === "answered" && filteredAnswered.length === 0 ? <div className="doctor-community-empty">No answered questions</div> : null}
                {(activeTab === "open" ? filteredOpen : filteredAnswered).map((item) => renderQuestionCard(item, activeTab))}
              </div>
              <div className="doctor-community-detail">
                {selectedQuestion ? (
                  <>
                    <h2>{activeTab === "open" ? "Reply to Patient" : "Answered Question"}</h2>
                    <div className="doctor-community-preview">
                      <strong>{getPatientName(selectedQuestion)}</strong>
                      <p>{getQuestionText(selectedQuestion)}</p>
                      {activeTab === "answered" && getAnswerPreview(selectedQuestion) ? <p><strong>Reply:</strong> {getAnswerPreview(selectedQuestion)}</p> : null}
                    </div>
                    {activeTab === "open" ? (
                      <div className="doctor-community-form">
                        <textarea value={replyText} onChange={(event) => setReplyText(event.target.value)} placeholder="Write your reply..." />
                        {fieldError ? <span className="doctor-community-validation">{fieldError}</span> : null}
                        <div className="doctor-community-actions">
                          <input className="doctor-community-file" type="file" onChange={(event) => setAttachment(event.target.files?.[0] || null)} />
                          <button type="button" className="doctor-community-secondary" onClick={() => setReplyText("")}>Cancel</button>
                          <button type="button" className="doctor-community-primary" disabled={sending} onClick={handleQuestionReply}>
                            {sending ? "Sending..." : "Send Reply"}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="doctor-community-empty">Select a question to view details</div>
                )}
              </div>
            </div>
          ) : (
            <div className="doctor-community-chat">
              <div className="doctor-community-conversations">
                {conversations.length === 0 ? <div className="doctor-community-empty">No messages yet</div> : null}
                {conversations.map((item) => {
                  const patientId = getPatientId(item);
                  return (
                    <button
                      type="button"
                      key={`patient-${patientId}`}
                      className={`doctor-community-card ${String(selectedPatientId) === String(patientId) ? "active" : ""}`}
                      onClick={() => {
                        setSelectedPatientId(patientId);
                        setSelectedConversation(item);
                        setFieldError("");
                        fetchMessages(patientId);
                      }}
                    >
                      <div className="doctor-community-card-head">
                        <span className="doctor-community-patient">{getPatientName(item)}</span>
                      </div>
                      <p className="doctor-community-question">{getAnswerPreview(item) || getQuestionText(item)}</p>
                    </button>
                  );
                })}
              </div>
              <div className="doctor-community-chat-window">
                <h2>Doctor Chats</h2>
                <div className="doctor-community-messages">
                  {messageLoading ? <div className="doctor-community-loading">Loading messages...</div> : null}
                  {!messageLoading && messages.length === 0 ? <div className="doctor-community-empty">No messages yet</div> : null}
                  {messages.map((message, index) => {
                    const senderId = message?.sender_id || message?.from_user_id || message?.user_id;
                    const fromDoctor = String(senderId) === String(doctorId) || message?.sender_type === "doctor" || message?.from === "doctor";
                    return (
                      <div key={`${message?.id || index}`} className={`doctor-community-message ${fromDoctor ? "doctor" : "patient"}`}>
                        {message?.message || message?.text || message?.content || ""}
                      </div>
                    );
                  })}
                </div>
                <div className="doctor-community-chat-input">
                  <textarea value={chatText} onChange={(event) => setChatText(event.target.value)} placeholder="Write a message..." />
                  <input className="doctor-community-file" type="file" onChange={(event) => setChatAttachment(event.target.files?.[0] || null)} />
                  <button type="button" className="doctor-community-primary" disabled={sending || !selectedPatientId} onClick={handleChatSend}>
                    {sending ? "Sending..." : "Send"}
                  </button>
                </div>
                {fieldError ? <span className="doctor-community-validation">{fieldError}</span> : null}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default DoctorCommunity;
